import type { DataRepository } from '@/types/repository';
import type {
  Manuscript,
  Series,
  Collection,
  PlannerStructure,
  CodexEntry,
  Goals,
  FormatSettings,
  SceneContent,
} from '@/types/database';
import type { ManuscriptBackupPayload } from '@/lib/backupPayload';
import { supabase } from '@/integrations/supabase/client';
import { purgeCloudManuscript } from '@/lib/repository/purgeCloud';
import { logger } from '@/lib/logger';
import { getE2ECryptoKey } from '@/lib/cryptoKeyStore';
import {
  getE2ESetupKey,
  getLastSyncedKey,
  getLastSyncedHashKey,
  getEntityHashesKey,
} from '@/utils/storageKeys';
import {
  encrypt as cryptoEncrypt,
  decrypt as cryptoDecrypt,
  generateSalt,
  deriveKey,
  APP_ENCRYPTION_SALT,
  type EncryptedBlob,
} from '@/lib/crypto';
import { createConflictCopy } from '@/lib/conflictCopier';
import { getSha256, stringifyCanonicalJson } from '@/utils/hash';
import { computeEntityHashes, smartMergePayloads } from '@/lib/smartMerge';
import {
  getDeviceId,
  getLastSyncedRevision,
  setLastSyncedRevision,
  getLastSyncedBaseHashes,
  setLastSyncedBaseHashes,
  commitManuscriptHeadRpc,
  decryptPayloadString,
} from '@/lib/revisionedSync';

interface WebPayloadOutboxEntry {
  version: 1;
  kind: 'payload';
  userId: string;
  manuscriptId: string;
  generation: number;
  updatedAt: string;
  payload: ManuscriptBackupPayload;
}

interface WebTombstoneOutboxEntry {
  version: 1;
  kind: 'tombstone';
  userId: string;
  manuscriptId: string;
  generation: number;
  updatedAt: string;
  deletedAt: string;
  expectedRevision: number;
  purgeCloud: boolean;
}

type WebOutboxEntry =
  | WebPayloadOutboxEntry
  | WebTombstoneOutboxEntry;

const payloadCache =
  new Map<string, ManuscriptBackupPayload>();
const standaloneSeriesCache = new Map<string, Series>();
const standaloneCollectionCache = new Map<string, Collection>();

let cacheUserId: string | null = null;
let isListenersInitialized = false;
let resumedOutboxUserId: string | null = null;

const saveDebounceTimers = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

const inFlightUpsertPromises =
  new Map<string, Promise<void>>();

const DEBOUNCE_DELAY_MS = 3000;
const RETRY_DELAY_MS = 5000;
const WEB_OUTBOX_PREFIX = 'birch_web_sync_outbox_v1_';

async function getUserId(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  return session?.user?.id ?? null;
}

async function parseAndDecryptPayload(
  contentStr: string,
  isEncrypted: boolean,
  userId: string
): Promise<ManuscriptBackupPayload | null> {
  try {
    let payloadText = contentStr;

    if (isEncrypted) {
      const parsed = JSON.parse(contentStr);

      if (
        parsed &&
        typeof parsed === 'object' &&
        !parsed.ciphertext &&
        (
          parsed.manuscript ||
          parsed.plannerStructure ||
          parsed.scenes ||
          parsed.id
        )
      ) {
        return parsed as ManuscriptBackupPayload;
      }

      const encryptedBlob: EncryptedBlob = parsed;
      const masterKey = getE2ECryptoKey(userId);
      let decrypted: string | null = null;

      if (masterKey) {
        try {
          decrypted = await cryptoDecrypt(
            encryptedBlob,
            masterKey
          );
        } catch {
          // Continue with legacy migration derivation.
        }
      }

      if (!decrypted) {
        try {
          const legacyKey = await deriveKey(
            userId,
            APP_ENCRYPTION_SALT
          );
          decrypted = await cryptoDecrypt(
            encryptedBlob,
            legacyKey
          );
        } catch (legacyErr) {
          console.error(
            '[WebRepository] Legacy decryption failed:',
            legacyErr
          );
        }
      }

      if (!decrypted) {
        console.error(
          '[WebRepository] Decryption failed with both the active and legacy keys.'
        );
        return null;
      }

      payloadText = decrypted;
    }

    return JSON.parse(
      payloadText
    ) as ManuscriptBackupPayload;
  } catch (err) {
    console.error(
      '[WebRepository] Failed to parse or decrypt payload:',
      err
    );
    return null;
  }
}

export class WebRepository implements DataRepository {
  private visibilityHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private unloadHandler: (() => void) | null = null;
  private periodicIntervalId:
    | ReturnType<typeof setInterval>
    | null = null;
  private inFlightRefreshPromise: Promise<void> | null = null;

  constructor() {
    this.setupCacheRefreshListeners();
    void this.resumeForCurrentUser();
  }

  private getOutboxKey(
    userId: string,
    manuscriptId: string
  ): string {
    return `${WEB_OUTBOX_PREFIX}${userId}_${manuscriptId}`;
  }

  private readOutboxEntry(
    userId: string,
    manuscriptId: string
  ): WebOutboxEntry | null {
    try {
      const raw = localStorage.getItem(
        this.getOutboxKey(userId, manuscriptId)
      );

      if (!raw) return null;

      const parsed = JSON.parse(raw) as WebOutboxEntry;

      if (
        parsed.version !== 1 ||
        parsed.userId !== userId ||
        parsed.manuscriptId !== manuscriptId ||
        !Number.isInteger(parsed.generation) ||
        parsed.generation < 1
      ) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  private getAllOutboxEntries(
    userId: string
  ): WebOutboxEntry[] {
    const prefix = `${WEB_OUTBOX_PREFIX}${userId}_`;
    const entries: WebOutboxEntry[] = [];

    try {
      for (
        let index = 0;
        index < localStorage.length;
        index += 1
      ) {
        const key = localStorage.key(index);

        if (!key?.startsWith(prefix)) continue;

        const manuscriptId = key.slice(prefix.length);
        const entry = this.readOutboxEntry(
          userId,
          manuscriptId
        );

        if (entry) {
          entries.push(entry);
        }
      }
    } catch (err) {
      logger.log(
        '[WebRepository] Failed to enumerate durable web outbox:',
        err
      );
    }

    return entries;
  }

  private writeOutboxEntry(entry: WebOutboxEntry): void {
    localStorage.setItem(
      this.getOutboxKey(
        entry.userId,
        entry.manuscriptId
      ),
      JSON.stringify(entry)
    );
  }

  private removeOutboxIfGenerationMatches(
    userId: string,
    manuscriptId: string,
    generation: number
  ): void {
    const current = this.readOutboxEntry(
      userId,
      manuscriptId
    );

    if (current?.generation === generation) {
      localStorage.removeItem(
        this.getOutboxKey(userId, manuscriptId)
      );
    }
  }

  private persistPayloadOutbox(
    userId: string,
    manuscriptId: string,
    payload: ManuscriptBackupPayload
  ): WebPayloadOutboxEntry {
    const current = this.readOutboxEntry(
      userId,
      manuscriptId
    );

    const entry: WebPayloadOutboxEntry = {
      version: 1,
      kind: 'payload',
      userId,
      manuscriptId,
      generation: (current?.generation ?? 0) + 1,
      updatedAt: new Date().toISOString(),
      payload,
    };

    this.writeOutboxEntry(entry);
    return entry;
  }

  private persistTombstoneOutbox(
    userId: string,
    manuscriptId: string,
    purgeCloud: boolean
  ): WebTombstoneOutboxEntry {
    const current = this.readOutboxEntry(
      userId,
      manuscriptId
    );
    const deletedAt = new Date().toISOString();

    const entry: WebTombstoneOutboxEntry = {
      version: 1,
      kind: 'tombstone',
      userId,
      manuscriptId,
      generation: (current?.generation ?? 0) + 1,
      updatedAt: deletedAt,
      deletedAt,
      expectedRevision:
        getLastSyncedRevision(manuscriptId),
      purgeCloud,
    };

    this.writeOutboxEntry(entry);
    return entry;
  }

  private async resumeForCurrentUser(): Promise<void> {
    const userId = await getUserId();

    if (userId) {
      cacheUserId = userId;
      await this.resumeDurableOutbox(userId);
    }
  }

  private async resumeDurableOutbox(
    userId: string,
    force: boolean = false
  ): Promise<void> {
    if (!force && resumedOutboxUserId === userId) {
      return;
    }

    resumedOutboxUserId = userId;

    const entries = this.getAllOutboxEntries(userId);

    for (const entry of entries) {
      if (entry.kind === 'payload') {
        payloadCache.set(
          entry.manuscriptId,
          entry.payload
        );
        this.saveLocalCacheFallback(
          userId,
          entry.manuscriptId,
          entry.payload
        );
        this.scheduleOutboxEntry(entry, 0);
      } else {
        payloadCache.delete(entry.manuscriptId);
        this.removeLocalCacheFallback(
          userId,
          entry.manuscriptId
        );
        void this.executeDurableOutboxEntry(entry);
      }
    }
  }

  private setupCacheRefreshListeners(): void {
    if (
      isListenersInitialized ||
      typeof window === 'undefined'
    ) {
      return;
    }

    isListenersInitialized = true;

    supabase.auth.onAuthStateChange(
      (_event, session) => {
        const newUserId = session?.user?.id ?? null;

        if (newUserId === cacheUserId) return;

        this.clearCacheOnAuthChange();
        cacheUserId = newUserId;

        if (newUserId) {
          window.setTimeout(() => {
            void this.resumeDurableOutbox(
              newUserId,
              true
            );
          }, 0);
        }
      }
    );

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        logger.log(
          '[WebRepository] Tab visible — refreshing cache.'
        );
        void this.refreshCache();
      }
    };

    window.addEventListener(
      'visibilitychange',
      this.visibilityHandler
    );

    this.onlineHandler = () => {
      logger.log(
        '[WebRepository] Network online — replaying durable outbox and refreshing cache.'
      );

      if (cacheUserId) {
        void this.resumeDurableOutbox(
          cacheUserId,
          true
        );
      }

      void this.refreshCache();
    };

    window.addEventListener('online', this.onlineHandler);

    this.unloadHandler = () => {
      this.flushPendingDebounces();
    };

    window.addEventListener(
      'beforeunload',
      this.unloadHandler
    );
    window.addEventListener(
      'pagehide',
      this.unloadHandler
    );

    this.periodicIntervalId = setInterval(() => {
      logger.log(
        '[WebRepository] Periodic cache refresh.'
      );
      void this.refreshCache();
    }, 2 * 60 * 1000);
  }

  public clearCacheOnAuthChange(): void {
    payloadCache.clear();
    standaloneSeriesCache.clear();
    standaloneCollectionCache.clear();

    for (const timer of saveDebounceTimers.values()) {
      clearTimeout(timer);
    }

    saveDebounceTimers.clear();
    cacheUserId = null;
    resumedOutboxUserId = null;
  }

  public destroy(): void {
    if (typeof window !== 'undefined') {
      if (this.visibilityHandler) {
        window.removeEventListener(
          'visibilitychange',
          this.visibilityHandler
        );
        this.visibilityHandler = null;
      }

      if (this.onlineHandler) {
        window.removeEventListener(
          'online',
          this.onlineHandler
        );
        this.onlineHandler = null;
      }

      if (this.unloadHandler) {
        window.removeEventListener(
          'beforeunload',
          this.unloadHandler
        );
        window.removeEventListener(
          'pagehide',
          this.unloadHandler
        );
        this.unloadHandler = null;
      }
    }

    if (this.periodicIntervalId) {
      clearInterval(this.periodicIntervalId);
      this.periodicIntervalId = null;
    }

    isListenersInitialized = false;
    this.clearCacheOnAuthChange();
  }

  public hasPendingWrites(
    manuscriptId?: string
  ): boolean {
    if (manuscriptId) {
      const operationSuffix = `:${manuscriptId}`;
      const hasInFlight = Array.from(
        inFlightUpsertPromises.keys()
      ).some((key) => key.endsWith(operationSuffix));

      return (
        saveDebounceTimers.has(manuscriptId) ||
        hasInFlight ||
        Boolean(
          cacheUserId &&
            this.readOutboxEntry(
              cacheUserId,
              manuscriptId
            )
        )
      );
    }

    return (
      saveDebounceTimers.size > 0 ||
      inFlightUpsertPromises.size > 0 ||
      Boolean(
        cacheUserId &&
          this.getAllOutboxEntries(cacheUserId).length
      )
    );
  }

  public flushPendingDebounces(): void {
    for (const timer of saveDebounceTimers.values()) {
      clearTimeout(timer);
    }

    saveDebounceTimers.clear();

    if (!cacheUserId) return;

    const entries =
      this.getAllOutboxEntries(cacheUserId);

    for (const entry of entries) {
      void this.executeDurableOutboxEntry(entry);
    }
  }

  public async refreshCache(): Promise<void> {
    if (this.inFlightRefreshPromise) {
      return this.inFlightRefreshPromise;
    }

    this.inFlightRefreshPromise = (async () => {
      try {
        const userId = await getUserId();

        if (!userId) return;

        cacheUserId = userId;
        await this.resumeDurableOutbox(userId);

        const { data, error } = await supabase
          .from('manuscript_heads')
          .select(
            'manuscript_id, revision, payload, is_encrypted, updated_at, deleted_at'
          )
          .eq('user_id', userId);

        if (error || !data) {
          if (error) {
            console.error(
              '[WebRepository] Failed to fetch manuscript heads:',
              error
            );
          }
          return;
        }

        for (const row of data) {
          if (
            row.manuscript_id === '__verification__'
          ) {
            continue;
          }

          const pending = this.readOutboxEntry(
            userId,
            row.manuscript_id
          );
          const serverRevision = Number(
            row.revision ?? 0
          );

          if (row.deleted_at) {
            if (pending?.kind === 'payload') {
              try {
                const conflictCopyId =
                  await createConflictCopy(
                    row.manuscript_id,
                    this
                  );

                logger.log(
                  `[WebRepository] Remote deletion won for ${row.manuscript_id}. Preserved unsynced local work as ${conflictCopyId}.`
                );

                this.removeOutboxIfGenerationMatches(
                  userId,
                  row.manuscript_id,
                  pending.generation
                );
                payloadCache.delete(row.manuscript_id);
                this.removeLocalCacheFallback(
                  userId,
                  row.manuscript_id
                );
                setLastSyncedRevision(
                  row.manuscript_id,
                  serverRevision
                );
              } catch (copyErr) {
                console.error(
                  '[WebRepository] Failed to preserve local work against a remote tombstone:',
                  copyErr
                );
                this.scheduleOutboxEntry(
                  pending,
                  RETRY_DELAY_MS
                );
              }

              continue;
            }

            if (pending?.kind === 'tombstone') {
              void this.executeDurableOutboxEntry(
                pending
              );
              continue;
            }

            payloadCache.delete(row.manuscript_id);
            this.removeLocalCacheFallback(
              userId,
              row.manuscript_id
            );
            setLastSyncedRevision(
              row.manuscript_id,
              serverRevision
            );
            continue;
          }

          if (pending) {
            if (pending.kind === 'payload') {
              payloadCache.set(
                row.manuscript_id,
                pending.payload
              );
            }

            this.scheduleOutboxEntry(pending, 0);
            continue;
          }

          const localRevision =
            getLastSyncedRevision(row.manuscript_id);

          if (
            serverRevision <= localRevision &&
            payloadCache.has(row.manuscript_id)
          ) {
            continue;
          }

          const payload = await parseAndDecryptPayload(
            row.payload,
            row.is_encrypted ?? false,
            userId
          );

          if (!payload?.manuscript) continue;

          payloadCache.set(row.manuscript_id, payload);
          this.saveLocalCacheFallback(
            userId,
            row.manuscript_id,
            payload
          );

          const payloadHash = await getSha256(
            stringifyCanonicalJson(payload)
          );
          const entityHashes =
            await computeEntityHashes(payload);

          setLastSyncedRevision(
            row.manuscript_id,
            serverRevision
          );
          setLastSyncedBaseHashes(
            row.manuscript_id,
            entityHashes
          );

          localStorage.setItem(
            getLastSyncedKey(row.manuscript_id),
            new Date(row.updated_at).toISOString()
          );
          localStorage.setItem(
            getLastSyncedHashKey(row.manuscript_id),
            payloadHash
          );
          localStorage.setItem(
            getEntityHashesKey(row.manuscript_id),
            JSON.stringify(entityHashes)
          );
        }
      } finally {
        this.inFlightRefreshPromise = null;
      }
    })();

    return this.inFlightRefreshPromise;
  }

  private saveLocalCacheFallback(
    userId: string,
    manuscriptId: string,
    payload: ManuscriptBackupPayload
  ): void {
    try {
      localStorage.setItem(
        `birch_local_ms_${userId}_${manuscriptId}`,
        JSON.stringify(payload)
      );

      const indexKey =
        `birch_local_ms_index_${userId}`;
      const indexString =
        localStorage.getItem(indexKey) ?? '[]';
      const index = JSON.parse(indexString) as string[];

      if (!index.includes(manuscriptId)) {
        index.push(manuscriptId);
        localStorage.setItem(
          indexKey,
          JSON.stringify(index)
        );
      }
    } catch (err) {
      console.warn(
        '[WebRepository] Failed to save local cache fallback:',
        err
      );
    }
  }

  private removeLocalCacheFallback(
    userId: string,
    manuscriptId: string
  ): void {
    try {
      localStorage.removeItem(
        `birch_local_ms_${userId}_${manuscriptId}`
      );

      const indexKey =
        `birch_local_ms_index_${userId}`;
      const index = JSON.parse(
        localStorage.getItem(indexKey) ?? '[]'
      ) as string[];
      const nextIndex = index.filter(
        (currentId) => currentId !== manuscriptId
      );

      localStorage.setItem(
        indexKey,
        JSON.stringify(nextIndex)
      );
    } catch (err) {
      logger.log(
        '[WebRepository] Failed to remove local cache fallback:',
        err
      );
    }
  }

  private loadLocalCacheFallback(userId: string): void {
    try {
      const indexString =
        localStorage.getItem(
          `birch_local_ms_index_${userId}`
        ) ?? '[]';
      const index = JSON.parse(indexString) as string[];

      for (const manuscriptId of index) {
        const raw = localStorage.getItem(
          `birch_local_ms_${userId}_${manuscriptId}`
        );

        if (!raw) continue;

        const payload = JSON.parse(
          raw
        ) as ManuscriptBackupPayload;

        if (payload?.manuscript) {
          payloadCache.set(manuscriptId, payload);
        }
      }

      const seriesRaw = localStorage.getItem(`birch_local_series_${userId}`);
      if (seriesRaw) {
        const seriesList = JSON.parse(seriesRaw) as Series[];
        for (const s of seriesList) {
          if (s?.id) standaloneSeriesCache.set(s.id, s);
        }
      }

      const collectionsRaw = localStorage.getItem(`birch_local_collections_${userId}`);
      if (collectionsRaw) {
        const collectionsList = JSON.parse(collectionsRaw) as Collection[];
        for (const c of collectionsList) {
          if (c?.id) standaloneCollectionCache.set(c.id, c);
        }
      }
    } catch (err) {
      console.warn(
        '[WebRepository] Failed to load local cache fallback:',
        err
      );
    }
  }

  private saveLocalSeriesFallback(userId: string | null): void {
    try {
      const key = `birch_local_series_${userId ?? 'anonymous'}`;
      const list = Array.from(standaloneSeriesCache.values());
      localStorage.setItem(key, JSON.stringify(list));
    } catch (err) {
      console.warn('[WebRepository] Failed to save local series fallback:', err);
    }
  }

  private saveLocalCollectionsFallback(userId: string | null): void {
    try {
      const key = `birch_local_collections_${userId ?? 'anonymous'}`;
      const list = Array.from(standaloneCollectionCache.values());
      localStorage.setItem(key, JSON.stringify(list));
    } catch (err) {
      console.warn('[WebRepository] Failed to save local collections fallback:', err);
    }
  }

  private async ensureCacheLoaded(): Promise<void> {
    const userId = await getUserId();

    if (!userId) {
      if (payloadCache.size === 0 && standaloneSeriesCache.size === 0 && standaloneCollectionCache.size === 0) {
        this.loadLocalCacheFallback('anonymous');
      }
      return;
    }

    if (cacheUserId && cacheUserId !== userId) {
      this.clearCacheOnAuthChange();
    }

    cacheUserId = userId;

    if (payloadCache.size === 0) {
      this.loadLocalCacheFallback(userId);
    }

    await this.resumeDurableOutbox(userId);

    if (payloadCache.size === 0) {
      await this.refreshCache();
    }
  }

  private scheduleOutboxEntry(
    entry: WebOutboxEntry,
    delayMs: number = DEBOUNCE_DELAY_MS
  ): void {
    const currentTimer =
      saveDebounceTimers.get(entry.manuscriptId);

    if (currentTimer) {
      clearTimeout(currentTimer);
    }

    const timer = setTimeout(() => {
      saveDebounceTimers.delete(
        entry.manuscriptId
      );

      const latest = this.readOutboxEntry(
        entry.userId,
        entry.manuscriptId
      );

      if (latest) {
        void this.executeDurableOutboxEntry(latest);
      }
    }, Math.max(0, delayMs));

    saveDebounceTimers.set(
      entry.manuscriptId,
      timer
    );
  }

  private async executeDurableOutboxEntry(
    requestedEntry: WebOutboxEntry
  ): Promise<void> {
    const operationKey =
      `${requestedEntry.userId}:${requestedEntry.manuscriptId}`;
    const inFlight =
      inFlightUpsertPromises.get(operationKey);

    if (inFlight) {
      return inFlight;
    }

    const latestEntry = this.readOutboxEntry(
      requestedEntry.userId,
      requestedEntry.manuscriptId
    );

    if (
      !latestEntry ||
      latestEntry.generation !==
        requestedEntry.generation
    ) {
      if (latestEntry) {
        this.scheduleOutboxEntry(latestEntry, 0);
      }

      return;
    }

    const operation = (async () => {
      try {
        const activeUserId = await getUserId();

        if (activeUserId !== latestEntry.userId) {
          return;
        }

        if (latestEntry.kind === 'tombstone') {
          await this.executeTombstone(latestEntry);
        } else {
          await this.executePayloadCommit(latestEntry);
        }
      } catch (err) {
        console.error(
          `[WebRepository] Durable sync operation failed for ${latestEntry.manuscriptId}:`,
          err
        );
      } finally {
        inFlightUpsertPromises.delete(operationKey);

        const pending = this.readOutboxEntry(
          latestEntry.userId,
          latestEntry.manuscriptId
        );

        if (pending) {
          this.scheduleOutboxEntry(
            pending,
            pending.generation >
              latestEntry.generation
              ? DEBOUNCE_DELAY_MS
              : RETRY_DELAY_MS
          );
        }
      }
    })();

    inFlightUpsertPromises.set(
      operationKey,
      operation
    );

    return operation;
  }

  private async executeTombstone(
    entry: WebTombstoneOutboxEntry
  ): Promise<void> {
    const result = await commitManuscriptHeadRpc(
      entry.manuscriptId,
      entry.expectedRevision,
      '{}',
      'tombstone',
      false,
      getDeviceId(),
      entry.deletedAt
    );

    if (result.accepted) {
      if (entry.purgeCloud) {
        await purgeCloudManuscript(
          entry.userId,
          entry.manuscriptId
        );
      }

      this.removeOutboxIfGenerationMatches(
        entry.userId,
        entry.manuscriptId,
        entry.generation
      );
      return;
    }

    const current = this.readOutboxEntry(
      entry.userId,
      entry.manuscriptId
    );

    if (
      current?.kind === 'tombstone' &&
      current.generation === entry.generation
    ) {
      this.writeOutboxEntry({
        ...current,
        expectedRevision: result.currentRevision,
        updatedAt: new Date().toISOString(),
      });
    }
  }

  private async executePayloadCommit(
    entry: WebPayloadOutboxEntry
  ): Promise<void> {
    let currentPayload = entry.payload;
    const plaintext =
      stringifyCanonicalJson(currentPayload);
    const payloadHash = await getSha256(plaintext);
    const isE2E =
      localStorage.getItem(
        getE2ESetupKey(entry.userId)
      ) === 'true';

    let contentStr = plaintext;
    let isEncrypted = false;

    if (isE2E) {
      const masterKey =
        getE2ECryptoKey(entry.userId);

      if (!masterKey) {
        logger.log(
          '[WebRepository] E2E key is locked. Durable payload remains queued.'
        );
        return;
      }

      const encryptedBlob = await cryptoEncrypt(
        plaintext,
        masterKey,
        generateSalt()
      );
      contentStr = JSON.stringify(encryptedBlob);
      isEncrypted = true;
    }

    const deviceId = getDeviceId();
    const expectedRevision =
      getLastSyncedRevision(entry.manuscriptId);

    const commitResult =
      await commitManuscriptHeadRpc(
        entry.manuscriptId,
        expectedRevision,
        contentStr,
        payloadHash,
        isEncrypted,
        deviceId
      );

    if (
      !commitResult.accepted &&
      (
        commitResult as {
          currentDeletedAt?: string | null;
        }
      ).currentDeletedAt
    ) {
      const conflictCopyId =
        await createConflictCopy(
          entry.manuscriptId,
          this
        );

      logger.log(
        `[WebRepository] Remote deletion won for ${entry.manuscriptId}. Preserved local changes as ${conflictCopyId}.`
      );

      payloadCache.delete(entry.manuscriptId);
      this.removeLocalCacheFallback(
        entry.userId,
        entry.manuscriptId
      );
      this.removeOutboxIfGenerationMatches(
        entry.userId,
        entry.manuscriptId,
        entry.generation
      );
      setLastSyncedRevision(
        entry.manuscriptId,
        commitResult.currentRevision
      );
      return;
    }

    if (
      !commitResult.accepted &&
      commitResult.currentPayload
    ) {
      const remotePayload =
        await decryptPayloadString(
          commitResult.currentPayload,
          Boolean(commitResult.currentIsEncrypted),
          entry.userId
        );

      if (!remotePayload) {
        throw new Error(
          'Unable to decrypt the remote conflict payload.'
        );
      }

      const baseHashes =
        getLastSyncedBaseHashes(
          entry.manuscriptId
        ) ?? {
          scenes: {},
          codex: {},
        };

      const mergeResult = await smartMergePayloads(
        currentPayload,
        remotePayload,
        baseHashes
      );

      if (mergeResult.hasConflicts) {
        await createConflictCopy(
          entry.manuscriptId,
          this
        );
      }

      currentPayload = mergeResult.merged;

      const currentEntry = this.readOutboxEntry(
        entry.userId,
        entry.manuscriptId
      );

      if (
        currentEntry?.kind === 'payload' &&
        currentEntry.generation === entry.generation
      ) {
        const updatedEntry: WebPayloadOutboxEntry = {
          ...currentEntry,
          payload: currentPayload,
          updatedAt: new Date().toISOString(),
        };

        this.writeOutboxEntry(updatedEntry);
        payloadCache.set(
          entry.manuscriptId,
          currentPayload
        );
        this.saveLocalCacheFallback(
          entry.userId,
          entry.manuscriptId,
          currentPayload
        );
      }

      const mergedJson =
        stringifyCanonicalJson(currentPayload);
      const mergedHash =
        await getSha256(mergedJson);

      let retryContentStr = mergedJson;
      let retryIsEncrypted = false;

      if (isE2E) {
        const masterKey =
          getE2ECryptoKey(entry.userId);

        if (!masterKey) {
          throw new Error(
            'The encryption key became unavailable during conflict resolution.'
          );
        }

        const encryptedBlob = await cryptoEncrypt(
          mergedJson,
          masterKey,
          generateSalt()
        );
        retryContentStr =
          JSON.stringify(encryptedBlob);
        retryIsEncrypted = true;
      }

      const retryResult =
        await commitManuscriptHeadRpc(
          entry.manuscriptId,
          commitResult.currentRevision,
          retryContentStr,
          mergedHash,
          retryIsEncrypted,
          deviceId
        );

      if (!retryResult.accepted) return;

      const entityHashes =
        await computeEntityHashes(currentPayload);

      setLastSyncedRevision(
        entry.manuscriptId,
        retryResult.currentRevision
      );
      setLastSyncedBaseHashes(
        entry.manuscriptId,
        entityHashes
      );

      localStorage.setItem(
        getLastSyncedKey(entry.manuscriptId),
        retryResult.currentUpdatedAt ??
          new Date().toISOString()
      );
      localStorage.setItem(
        getLastSyncedHashKey(entry.manuscriptId),
        mergedHash
      );
      localStorage.setItem(
        getEntityHashesKey(entry.manuscriptId),
        JSON.stringify(entityHashes)
      );

      this.removeOutboxIfGenerationMatches(
        entry.userId,
        entry.manuscriptId,
        entry.generation
      );
      return;
    }

    if (!commitResult.accepted) {
      if (commitResult.currentRevision === 0) {
        setLastSyncedRevision(
          entry.manuscriptId,
          0
        );
      }

      return;
    }

    const entityHashes =
      await computeEntityHashes(currentPayload);

    setLastSyncedRevision(
      entry.manuscriptId,
      commitResult.currentRevision
    );
    setLastSyncedBaseHashes(
      entry.manuscriptId,
      entityHashes
    );

    localStorage.setItem(
      getLastSyncedKey(entry.manuscriptId),
      commitResult.currentUpdatedAt ??
        new Date().toISOString()
    );
    localStorage.setItem(
      getLastSyncedHashKey(entry.manuscriptId),
      payloadHash
    );
    localStorage.setItem(
      getEntityHashesKey(entry.manuscriptId),
      JSON.stringify(entityHashes)
    );

    this.removeOutboxIfGenerationMatches(
      entry.userId,
      entry.manuscriptId,
      entry.generation
    );
  }

  public async importBackupPayload(
    payload: ManuscriptBackupPayload
  ): Promise<void> {
    await this.ensureCacheLoaded();

    if (!payload.manuscript?.id) return;

    const existing =
      payloadCache.get(payload.manuscript.id);

    const merged: ManuscriptBackupPayload = {
      manuscript: payload.manuscript,
      plannerStructure:
        payload.plannerStructure ??
        existing?.plannerStructure ?? {
          id: payload.manuscript.id,
          structure: [],
        },
      sceneContent:
        payload.sceneContent?.length
          ? payload.sceneContent
          : existing?.sceneContent ?? [],
      codexEntries:
        payload.codexEntries?.length
          ? payload.codexEntries
          : existing?.codexEntries ?? [],
      goals: payload.goals ?? existing?.goals,
      formatSettings:
        payload.formatSettings ??
        existing?.formatSettings,
      series: payload.series ?? existing?.series,
      collection:
        payload.collection ??
        existing?.collection,
    };

    await this.savePayloadToCloud(
      payload.manuscript.id,
      merged,
      true
    );
  }

  private async savePayloadToCloud(
    manuscriptId: string,
    payload: ManuscriptBackupPayload,
    immediate: boolean = false
  ): Promise<void> {
    const userId =
      cacheUserId ?? (await getUserId());

    if (!userId) {
      payloadCache.set(manuscriptId, payload);
      this.saveLocalCacheFallback(
        'anonymous',
        manuscriptId,
        payload
      );
      return;
    }

    const entry = this.persistPayloadOutbox(
      userId,
      manuscriptId,
      payload
    );

    payloadCache.set(manuscriptId, payload);
    this.saveLocalCacheFallback(
      userId,
      manuscriptId,
      payload
    );

    const existingTimer =
      saveDebounceTimers.get(manuscriptId);

    if (existingTimer) {
      clearTimeout(existingTimer);
      saveDebounceTimers.delete(manuscriptId);
    }

    if (immediate) {
      await this.executeDurableOutboxEntry(entry);
    } else {
      this.scheduleOutboxEntry(entry);
    }
  }

  async getAllManuscripts(): Promise<Manuscript[]> {
    await this.ensureCacheLoaded();

    const manuscripts: Manuscript[] = [];

    for (const payload of payloadCache.values()) {
      if (payload.manuscript) {
        manuscripts.push(payload.manuscript);
      }
    }

    return manuscripts;
  }

  async getManuscript(
    id: string
  ): Promise<Manuscript | undefined> {
    await this.ensureCacheLoaded();
    return payloadCache.get(id)?.manuscript;
  }

  async putManuscript(
    manuscript: Manuscript
  ): Promise<void> {
    await this.ensureCacheLoaded();

    const existing = payloadCache.get(manuscript.id);
    const payload: ManuscriptBackupPayload =
      existing
        ? {
            ...existing,
            manuscript,
          }
        : {
            manuscript,
            plannerStructure: {
              id: manuscript.id,
              structure: [],
            },
            codexEntries: [],
            goals: undefined,
            formatSettings: undefined,
            sceneContent: [],
          };

    await this.savePayloadToCloud(
      manuscript.id,
      payload,
      true
    );
  }

  async deleteManuscript(
    id: string,
    purgeCloud: boolean = true
  ): Promise<void> {
    await this.ensureCacheLoaded();

    const userId =
      cacheUserId ?? (await getUserId());

    if (!userId) {
      payloadCache.delete(id);
      this.removeLocalCacheFallback('anonymous', id);
      return;
    }

    const entry = this.persistTombstoneOutbox(
      userId,
      id,
      purgeCloud
    );

    const timer = saveDebounceTimers.get(id);

    if (timer) {
      clearTimeout(timer);
      saveDebounceTimers.delete(id);
    }

    payloadCache.delete(id);
    this.removeLocalCacheFallback(userId, id);

    localStorage.removeItem(getLastSyncedKey(id));
    localStorage.removeItem(
      getLastSyncedHashKey(id)
    );
    localStorage.removeItem(
      getEntityHashesKey(id)
    );

    const operationKey = `${userId}:${id}`;
    const existingOperation =
      inFlightUpsertPromises.get(operationKey);

    if (existingOperation) {
      await existingOperation;
    }

    const latest =
      this.readOutboxEntry(userId, id) ?? entry;

    await this.executeDurableOutboxEntry(latest);
  }

  public async applyRemoteTombstone(
    id: string
  ): Promise<void> {
    const userId =
      cacheUserId ?? (await getUserId());

    if (!userId) return;

    const timer = saveDebounceTimers.get(id);

    if (timer) {
      clearTimeout(timer);
      saveDebounceTimers.delete(id);
    }

    payloadCache.delete(id);
    this.removeLocalCacheFallback(userId, id);
    localStorage.removeItem(
      this.getOutboxKey(userId, id)
    );
    localStorage.removeItem(getLastSyncedKey(id));
    localStorage.removeItem(
      getLastSyncedHashKey(id)
    );
    localStorage.removeItem(
      getEntityHashesKey(id)
    );
  }

  async getAllSeries(): Promise<Series[]> {
    await this.ensureCacheLoaded();

    const seriesMap = new Map<string, Series>(standaloneSeriesCache);

    for (const payload of payloadCache.values()) {
      if (payload.series) {
        seriesMap.set(
          payload.series.id,
          payload.series
        );
      }
    }

    return Array.from(seriesMap.values());
  }

  async getSeries(
    id: string
  ): Promise<Series | undefined> {
    const allSeries = await this.getAllSeries();
    return allSeries.find((series) => series.id === id);
  }

  async putSeries(series: Series): Promise<void> {
    await this.ensureCacheLoaded();

    standaloneSeriesCache.set(series.id, series);
    const userId = cacheUserId ?? (await getUserId());
    this.saveLocalSeriesFallback(userId);

    if (userId) {
      for (const [manuscriptId, payload] of payloadCache) {
        if (series.manuscripts.includes(manuscriptId)) {
          await this.savePayloadToCloud(
            manuscriptId,
            { ...payload, series },
            true
          );
        } else if (payload.series?.id === series.id) {
          await this.savePayloadToCloud(
            manuscriptId,
            { ...payload, series: undefined },
            true
          );
        }
      }
    } else {
      for (const [manuscriptId, payload] of payloadCache) {
        if (series.manuscripts.includes(manuscriptId)) {
          payloadCache.set(manuscriptId, { ...payload, series });
        } else if (payload.series?.id === series.id) {
          payloadCache.set(manuscriptId, { ...payload, series: undefined });
        }
      }
    }
  }

  async deleteSeries(id: string): Promise<void> {
    await this.ensureCacheLoaded();

    standaloneSeriesCache.delete(id);
    const userId = cacheUserId ?? (await getUserId());
    this.saveLocalSeriesFallback(userId);

    if (userId) {
      for (const [manuscriptId, payload] of payloadCache) {
        if (payload.series?.id === id) {
          await this.savePayloadToCloud(
            manuscriptId,
            {
              ...payload,
              series: undefined,
            },
            true
          );
        }
      }
    } else {
      for (const [manuscriptId, payload] of payloadCache) {
        if (payload.series?.id === id) {
          payloadCache.set(manuscriptId, { ...payload, series: undefined });
        }
      }
    }
  }

  async getAllCollections(): Promise<Collection[]> {
    await this.ensureCacheLoaded();

    const collectionMap =
      new Map<string, Collection>(standaloneCollectionCache);

    for (const payload of payloadCache.values()) {
      if (payload.collection) {
        collectionMap.set(
          payload.collection.id,
          payload.collection
        );
      }
    }

    return Array.from(collectionMap.values());
  }

  async getCollection(
    id: string
  ): Promise<Collection | undefined> {
    const allCollections =
      await this.getAllCollections();

    return allCollections.find(
      (collection) => collection.id === id
    );
  }

  async putCollection(
    collection: Collection
  ): Promise<void> {
    await this.ensureCacheLoaded();

    standaloneCollectionCache.set(collection.id, collection);
    const userId = cacheUserId ?? (await getUserId());
    this.saveLocalCollectionsFallback(userId);

    if (userId) {
      for (const [manuscriptId, payload] of payloadCache) {
        if (
          collection.manuscripts.includes(
            manuscriptId
          )
        ) {
          await this.savePayloadToCloud(
            manuscriptId,
            { ...payload, collection },
            true
          );
        } else if (payload.collection?.id === collection.id) {
          await this.savePayloadToCloud(
            manuscriptId,
            { ...payload, collection: undefined },
            true
          );
        }
      }
    } else {
      for (const [manuscriptId, payload] of payloadCache) {
        if (collection.manuscripts.includes(manuscriptId)) {
          payloadCache.set(manuscriptId, { ...payload, collection });
        } else if (payload.collection?.id === collection.id) {
          payloadCache.set(manuscriptId, { ...payload, collection: undefined });
        }
      }
    }
  }

  async deleteCollection(id: string): Promise<void> {
    await this.ensureCacheLoaded();

    standaloneCollectionCache.delete(id);
    const userId = cacheUserId ?? (await getUserId());
    this.saveLocalCollectionsFallback(userId);

    if (userId) {
      for (const [manuscriptId, payload] of payloadCache) {
        if (payload.collection?.id === id) {
          await this.savePayloadToCloud(
            manuscriptId,
            {
              ...payload,
              collection: undefined,
            },
            true
          );
        }
      }
    } else {
      for (const [manuscriptId, payload] of payloadCache) {
        if (payload.collection?.id === id) {
          payloadCache.set(manuscriptId, { ...payload, collection: undefined });
        }
      }
    }
  }

  async getPlannerStructure(
    manuscriptId: string
  ): Promise<PlannerStructure | undefined> {
    await this.ensureCacheLoaded();

    return payloadCache.get(manuscriptId)
      ?.plannerStructure;
  }

  async putPlannerStructure(
    structure: PlannerStructure
  ): Promise<void> {
    await this.ensureCacheLoaded();

    const payload = payloadCache.get(structure.id);

    if (payload) {
      await this.savePayloadToCloud(
        structure.id,
        {
          ...payload,
          plannerStructure: structure,
        }
      );
    }
  }

  async getCodexEntriesForManuscript(
    manuscriptId: string
  ): Promise<CodexEntry[]> {
    await this.ensureCacheLoaded();

    return (
      payloadCache.get(manuscriptId)
        ?.codexEntries ?? []
    );
  }

  async getCodexEntry(
    id: string
  ): Promise<CodexEntry | undefined> {
    await this.ensureCacheLoaded();

    for (const payload of payloadCache.values()) {
      const entry = payload.codexEntries.find(
        (candidate) => candidate.id === id
      );

      if (entry) return entry;
    }

    return undefined;
  }

  async putCodexEntry(entry: CodexEntry): Promise<void> {
    await this.ensureCacheLoaded();

    const payload = payloadCache.get(
      entry.manuscriptId
    );

    if (!payload) return;

    const filtered = payload.codexEntries.filter(
      (candidate) => candidate.id !== entry.id
    );

    await this.savePayloadToCloud(
      entry.manuscriptId,
      {
        ...payload,
        codexEntries: [...filtered, entry],
      }
    );
  }

  async deleteCodexEntry(id: string): Promise<void> {
    await this.ensureCacheLoaded();

    for (const [manuscriptId, payload] of payloadCache) {
      if (
        payload.codexEntries.some(
          (entry) => entry.id === id
        )
      ) {
        await this.savePayloadToCloud(
          manuscriptId,
          {
            ...payload,
            codexEntries:
              payload.codexEntries.filter(
                (entry) => entry.id !== id
              ),
          }
        );
      }
    }
  }

  async getGoals(
    manuscriptId: string
  ): Promise<Goals | undefined> {
    await this.ensureCacheLoaded();
    return payloadCache.get(manuscriptId)?.goals;
  }

  async putGoals(goals: Goals): Promise<void> {
    await this.ensureCacheLoaded();

    const payload = payloadCache.get(goals.id);

    if (payload) {
      await this.savePayloadToCloud(
        goals.id,
        { ...payload, goals }
      );
    }
  }

  async getFormatSettings(
    manuscriptId: string
  ): Promise<FormatSettings | undefined> {
    await this.ensureCacheLoaded();

    return payloadCache.get(manuscriptId)
      ?.formatSettings;
  }

  async putFormatSettings(
    settings: FormatSettings
  ): Promise<void> {
    await this.ensureCacheLoaded();

    const payload = payloadCache.get(settings.id);

    if (payload) {
      await this.savePayloadToCloud(
        settings.id,
        {
          ...payload,
          formatSettings: settings,
        }
      );
    }
  }

  async getSceneContent(
    sceneId: string
  ): Promise<SceneContent | undefined> {
    await this.ensureCacheLoaded();

    for (const payload of payloadCache.values()) {
      const scene = payload.sceneContent.find(
        (candidate) => candidate.id === sceneId
      );

      if (scene) return scene as SceneContent;
    }

    return undefined;
  }

  async putSceneContent(
    entry: SceneContent
  ): Promise<void> {
    await this.ensureCacheLoaded();

    const payload = payloadCache.get(
      entry.manuscriptId
    );

    if (!payload) return;

    const filtered = payload.sceneContent.filter(
      (scene) => scene.id !== entry.id
    );

    await this.savePayloadToCloud(
      entry.manuscriptId,
      {
        ...payload,
        sceneContent: [
          ...filtered,
          {
            id: entry.id,
            manuscriptId: entry.manuscriptId,
            content: entry.content,
          },
        ],
      }
    );
  }

  async deleteSceneContent(
    sceneId: string
  ): Promise<void> {
    await this.ensureCacheLoaded();

    for (const [manuscriptId, payload] of payloadCache) {
      if (
        payload.sceneContent.some(
          (scene) => scene.id === sceneId
        )
      ) {
        await this.savePayloadToCloud(
          manuscriptId,
          {
            ...payload,
            sceneContent:
              payload.sceneContent.filter(
                (scene) => scene.id !== sceneId
              ),
          }
        );
      }
    }
  }

  async getAllSceneContentForManuscript(
    manuscriptId: string
  ): Promise<SceneContent[]> {
    await this.ensureCacheLoaded();

    return (
      (payloadCache.get(manuscriptId)
        ?.sceneContent as SceneContent[]) ?? []
    );
  }
}

export const webRepository = new WebRepository();
